import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { debounce, styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import axios from "axios";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { getIndividualById } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import Forum from "messaging/forums/forum";
import ForumName from "notifications/values/forum-name/forum-name";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";

const ForumContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  margin: theme.spacing(2, 0),
  width: "100%",
}));
const AddSubscriberContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
  },
  minWidth: "15rem",
  paddingLeft: theme.spacing(4),
  textAlign: "left",
}));
const ForumNameCreate = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100%",
    "&:first-of-type": {
      marginBottom: theme.spacing(1.5),
      marginRight: 0,
    },
  },
  minWidth: theme.spacing(35),
}));
const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  padding: theme.spacing(4),
  paddingTop: 0,
  width: "100%",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const ConnectionListContainer = styled(List)(({ theme }) => ({
  maxHeight: "20rem",
  overflowY: "auto",
  padding: theme.spacing(1, 0),
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
}));

type CreateForumDialogProps = {
  onForumCreated: (createdForum: Forum) => void;
};

export default function CreateForumDialog(props: Readonly<CreateForumDialogProps>) {
  // #region State
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [forumNameValue, setForumNameValue] = useState<string>("");
  const [forumSubscribers, setForumSubscribers] = useState<UserInformation[]>([]);

  // #region Hooks
  const session = useSession();
  const { closeDialog } = useDialog();

  // #region Queries
  const forumCreator = useSelector(getIndividualById(session.user?.individualId));
  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();

  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data);
  }, [networkConnectionQuery.data]);

  function search(value: string) {
    setSearchTerm(value);
    if (value === "") return;
    debouncedSearch(value);
  }

  async function saveChanges(): Promise<void> {
    if (!forumCreator) return;

    setIsSaving(true);
    try {
      if (forumSubscribers.some((s) => !s.legalEntityId || !s.id))
        throw new Error("Subscriber is missing userId or entityId");

      const subscribers: EntityMember[] = forumSubscribers.map((individual) => {
        const legalEntityId = individual.legalEntityId;
        if (!legalEntityId) {
          throw new Error(`Individual ${individual.id} is missing entityId`);
        }
        return EntityMember.create({ userId: individual.id, legalEntityId });
      });
      const newForum = Forum.create({
        name: new ForumName(forumNameValue),
        subscribers: subscribers,
        createdDate: new AHDate(),
        creator: EntityMember.create({
          userId: forumCreator.userId,
          legalEntityId: forumCreator.legalEntityId,
        }),
        numUnreadMessages: 0,
      });
      const messagingService = new MessagingAPIService(session);
      const createdForum = await messagingService.createForum(newForum);
      props.onForumCreated(createdForum);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        enqueueSnackbar("Unable to create Forum: " + error.response.data, { variant: "warning" });
      } else {
        console.error(error);
        enqueueSnackbar("Unable to create Forum", { variant: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  }

  function handleSubscriberSelected(
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    subscriber: UserInformation
  ): void {
    const selectedSubscribers = [...forumSubscribers];
    const index = selectedSubscribers.findIndex((s) => s.id.isEqualTo(subscriber.id));
    if (index === -1) {
      selectedSubscribers.push(subscriber);
    } else {
      selectedSubscribers.splice(index, 1);
    }
    setForumSubscribers(selectedSubscribers);
  }

  function isSelected(subscriber: UserInformation): boolean {
    if (!subscriber.id) return false;
    return forumSubscribers.some((s) => s.id.isEqualTo(subscriber.id));
  }

  function renderSubscriberAvatar(individual?: Individual): React.ReactNode {
    return <IndividualAvatar individual={individual} session={session} />;
  }

  const saveDisabled: boolean = forumSubscribers.length < 1 || isSaving || !forumNameValue || !forumCreator;
  const debouncedSearch = debounce((_value) => {}, 100);
  const canCreateForum: boolean = networkConnections.length > 0;

  return (
    <React.Fragment>
      {networkConnectionQuery.isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!networkConnectionQuery.isLoading && !canCreateForum && (
        <LoaderContainer>
          <Typography>You are not networked with anyone</Typography>
        </LoaderContainer>
      )}
      {!networkConnectionQuery.isLoading && canCreateForum && (
        <>
          <ForumContainer>
            <ForumNameCreate
              label="Conversation name"
              required={true}
              helperText="required"
              onChange={(e) => {
                setForumNameValue(e.target.value);
              }}
              value={forumNameValue}
            />
            <AddSubscriberContainer>
              <SearchField
                size="small"
                variant="standard"
                placeholder="Search Connections"
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon sx={{ mr: 1 }} color="disabled" />,
                    endAdornment: (
                      <>
                        {searchTerm.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm("");
                              search("");
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </>
                    ),
                  },
                }}
                value={searchTerm}
                onChange={(event) => search(event.target.value)}
              />
              <ConnectionListContainer dense>
                {networkConnections.map((connection: UserInformation) => {
                  return (
                    <ListItemButton
                      disableGutters
                      key={connection.id?.value}
                      selected={isSelected(connection)}
                      onClick={(event) => handleSubscriberSelected(event, connection)}
                    >
                      <ListItemAvatar>
                        {isSelected(connection) ? (
                          <SelectedIcon>
                            <DoneIcon />
                          </SelectedIcon>
                        ) : (
                          renderSubscriberAvatar(connection.toIndividual())
                        )}
                      </ListItemAvatar>
                      <ListItemText primary={connection.name.fullName} secondary={connection.email?.value ?? ""} />
                    </ListItemButton>
                  );
                })}
              </ConnectionListContainer>
            </AddSubscriberContainer>
          </ForumContainer>
          <ButtonContainer>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              loading={isSaving}
              disabled={saveDisabled}
              onClick={saveChanges}
            >
              Save
            </LoadingButton>
            <Button variant="contained" color="primary" onClick={() => closeDialog()}>
              Cancel
            </Button>
          </ButtonContainer>
        </>
      )}
    </React.Fragment>
  );
}
